(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';
  var STORAGE_PREFIX = 'collection-nav:';

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function isMobile() {
    return window.matchMedia
      ? window.matchMedia(MOBILE_QUERY).matches
      : window.innerWidth <= 767;
  }

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Storage may be unavailable in private/restricted browsing.
      // Toggling must still continue to work.
    }
  }

  function normaliseState(value) {
    return value === 'open' ? 'open' : 'closed';
  }

  function getDefaultState(element) {
    var mobileDefault = element.getAttribute('data-default-mobile');
    var desktopDefault = element.getAttribute('data-default-desktop');

    if (isMobile()) {
      return normaliseState(mobileDefault || 'closed');
    }

    return normaliseState(desktopDefault || 'open');
  }

  function setExpanded(toggle, content, expanded, options) {
    options = options || {};

    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (expanded) {
      content.removeAttribute('hidden');
    } else {
      content.setAttribute('hidden', '');
    }

    if (options.storageKey) {
      safeStorageSet(
        options.storageKey,
        expanded ? 'open' : 'closed'
      );
    }
  }

  function getStoredOrDefaultState(storageKey, defaultElement) {
    var stored = safeStorageGet(storageKey);

    if (stored === 'open' || stored === 'closed') {
      return stored;
    }

    return getDefaultState(defaultElement);
  }

  function getCollectionKey(nav, index) {
    var key = nav.getAttribute('data-collection-key');

    if (key) {
      return key;
    }

    return 'collection-' + index;
  }

  function getGroupKey(group, toggle, index) {
    var key = group.getAttribute('data-category-group');

    if (key) {
      return key;
    }

    var controlledID = toggle.getAttribute('aria-controls');

    if (controlledID) {
      return controlledID;
    }

    return 'group-' + index;
  }

  function findControlledContent(nav, toggle) {
    var controlledID = toggle.getAttribute('aria-controls');

    if (!controlledID) {
      return null;
    }

    var content = document.getElementById(controlledID);

    if (!content || !nav.contains(content)) {
      return null;
    }

    return content;
  }

  function initialiseGroupedNavigation(nav, collectionKey) {
    var toggleNodes = nav.querySelectorAll('[data-category-group-toggle]');
    var groups = [];

    Array.prototype.forEach.call(toggleNodes, function (toggle, index) {
      var content = findControlledContent(nav, toggle);

      if (!content) {
        return;
      }

      var group = toggle.closest
        ? toggle.closest('.collection-subcategory-group')
        : null;

      if (!group) {
        return;
      }

      var groupKey = getGroupKey(group, toggle, index);
      var storageKey =
        STORAGE_PREFIX +
        collectionKey +
        ':group:' +
        groupKey;

      var initialState = getStoredOrDefaultState(storageKey, group);

      setExpanded(toggle, content, initialState === 'open');

      toggle.addEventListener('click', function () {
        var currentlyExpanded =
          toggle.getAttribute('aria-expanded') === 'true';

        setExpanded(toggle, content, !currentlyExpanded, {
          storageKey: storageKey
        });
      });

      groups.push({
        group: group,
        toggle: toggle,
        content: content,
        storageKey: storageKey
      });
    });

    var expandAll = nav.querySelector('[data-category-nav-expand-all]');
    var collapseAll = nav.querySelector('[data-category-nav-collapse-all]');

    if (expandAll) {
      expandAll.addEventListener('click', function () {
        groups.forEach(function (item) {
          setExpanded(item.toggle, item.content, true, {
            storageKey: item.storageKey
          });
        });
      });
    }

    if (collapseAll) {
      collapseAll.addEventListener('click', function () {
        groups.forEach(function (item) {
          setExpanded(item.toggle, item.content, false, {
            storageKey: item.storageKey
          });
        });
      });
    }

    return groups;
  }

  function initialiseFlatNavigation(nav, collectionKey) {
    if (nav.getAttribute('data-collapsible-flat-nav') !== 'true') {
      return null;
    }

    var toggle = nav.querySelector('[data-category-nav-toggle]');

    if (!toggle) {
      return null;
    }

    var content = findControlledContent(nav, toggle);

    if (!content) {
      return null;
    }

    var storageKey =
      STORAGE_PREFIX +
      collectionKey +
      ':flat';

    var stored = safeStorageGet(storageKey);
    var initialState;

    if (stored === 'open' || stored === 'closed') {
      initialState = stored;
    } else {
      /*
       * Default for large flat navigation:
       * desktop = open
       * mobile = closed
       */
      initialState = isMobile() ? 'closed' : 'open';
    }

    setExpanded(toggle, content, initialState === 'open');

    toggle.addEventListener('click', function () {
      var currentlyExpanded =
        toggle.getAttribute('aria-expanded') === 'true';

      setExpanded(toggle, content, !currentlyExpanded, {
        storageKey: storageKey
      });
    });

    return {
      toggle: toggle,
      content: content,
      storageKey: storageKey
    };
  }

  function initialiseJumpLinks(nav) {
    var jumpLinks = nav.querySelectorAll(
      '.collection-category-nav-jump, [data-category-nav-jump]'
    );

    Array.prototype.forEach.call(jumpLinks, function (link) {
      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href');

        if (!href || href.charAt(0) !== '#') {
          return;
        }

        var target = document.getElementById(href.substring(1));

        if (!target) {
          return;
        }

        /*
         * Native anchor behaviour already works.
         * Smooth scrolling is added only when supported and motion is allowed.
         */
        var reduceMotion =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (
          !reduceMotion &&
          typeof target.scrollIntoView === 'function'
        ) {
          event.preventDefault();

          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          try {
            window.history.replaceState(null, '', href);
          } catch (error) {
            // Ignore history API restrictions.
          }
        }
      });
    });
  }

  function initialiseNavigation(nav, index) {
    if (nav.getAttribute('data-category-nav-enhanced') === 'true') {
      return;
    }

    nav.setAttribute('data-category-nav-enhanced', 'true');

    var collectionKey = getCollectionKey(nav, index);

    initialiseGroupedNavigation(nav, collectionKey);
    initialiseFlatNavigation(nav, collectionKey);
    initialiseJumpLinks(nav);

    nav.classList.add('collection-category-nav--enhanced');
  }

  function initialiseAll() {
    var navs = document.querySelectorAll(
      '[data-collection-category-nav]'
    );

    Array.prototype.forEach.call(navs, function (nav, index) {
      initialiseNavigation(nav, index);
    });
  }

  ready(initialiseAll);
})();